import { consume } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { SchmancyEvents } from '@schmancy/types'
import { SchmancyTheme } from '@schmancy/theme'
import { color } from '@schmancy/directives'
import {
	SchmancyDrawerNavbarMode,
	SchmancyDrawerNavbarState,
	TSchmancyDrawerNavbarMode,
	TSchmancyDrawerNavbarState,
} from './context'

const ANIMATION_EASING = 'cubic-bezier(0.5, 0.01, 0.25, 1)'
const OVERLAY_ANIM_DURATION_OPEN = 200
const OVERLAY_ANIM_DURATION_CLOSE = 150
const NAV_ANIM_DURATION = 200

@customElement('schmancy-nav-drawer-navbar')
export class SchmancyNavigationDrawerSidebar extends SchmancyElement {
	@consume({ context: SchmancyDrawerNavbarMode, subscribe: true })
	@state()
	mode!: TSchmancyDrawerNavbarMode

	@consume({ context: SchmancyDrawerNavbarState, subscribe: true })
	@state()
	drawerState!: TSchmancyDrawerNavbarState

	@query('#overlay') overlay!: HTMLElement
	@query('nav') nav!: HTMLElement

	@property({ type: String }) width = '220px'
	@state() private initialized = false

	firstUpdated() {
		if (this.mode === 'overlay') {
			if (this.drawerState === 'close') {
				this.nav.style.transform = 'translateX(-100%)'
				this.overlay.style.display = 'none'
			} else if (this.drawerState === 'open') {
				this.nav.style.transform = 'translateX(0)'
				this.overlay.style.display = 'block'
				this.overlay.style.opacity = '0.4'
			}
		} else if (this.mode === 'push') {
			this.nav.style.transform = 'translateX(0)'
			this.overlay.style.display = 'none'
		}
		this.initialized = true
	}

	updated(changedProperties: Map<string, unknown>) {
		if (!this.initialized) return

		if (changedProperties.has('drawerState') || changedProperties.has('mode')) {
			if (this.mode === 'overlay') {
				if (this.drawerState === 'open') {
					if (this.nav.style.transform !== 'translateX(0)') {
						this.openOverlay()
						this.showNavDrawer()
					}
				} else if (this.drawerState === 'close') {
					if (this.nav.style.transform !== 'translateX(-100%)') {
						this.hideNavDrawer()
						this.closeOverlay()
					}
				}
			} else if (this.mode === 'push') {
				if (this.nav.style.transform !== 'translateX(0)') {
					this.showNavDrawer()
				}
				if (this.overlay.style.display !== 'none') {
					this.closeOverlay()
				}
			}
		}
	}

	openOverlay() {
		this.overlay.style.display = 'block'
		this.overlay.animate([{ opacity: 0 }, { opacity: 0.4 }], {
			duration: OVERLAY_ANIM_DURATION_OPEN,
			easing: ANIMATION_EASING,
			fill: 'forwards',
		})
	}

	closeOverlay() {
		const animation = this.overlay.animate([{ opacity: 0.4 }, { opacity: 0 }], {
			duration: OVERLAY_ANIM_DURATION_CLOSE,
			easing: ANIMATION_EASING,
			fill: 'forwards',
		})
		animation.onfinish = () => {
			this.overlay.style.display = 'none'
		}
	}
	showNavDrawer() {
		const animation = this.nav.animate([{ transform: 'translateX(-100%)' }, { transform: 'translateX(0)' }], {
			duration: NAV_ANIM_DURATION,
			easing: ANIMATION_EASING,
			fill: 'forwards',
		})
		animation.onfinish = () => {
			this.nav.style.transform = 'translateX(0)'
		}
	}

	hideNavDrawer() {
		const animation = this.nav.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-100%)' }], {
			duration: NAV_ANIM_DURATION,
			easing: ANIMATION_EASING,
			fill: 'forwards',
		})
		animation.onfinish = () => {
			this.nav.style.transform = 'translateX(-100%)'
		}
	}

	private handleOverlayClick() {
		window.dispatchEvent(
			new CustomEvent(SchmancyEvents.NavDrawer_toggle, {
				detail: { state: 'close' },
				bubbles: true,
				composed: true,
			}),
		)
	}

	protected render() {
		const sidebarClasses = {
			'max-w-[360px] w-fit h-full overflow-auto': true,
			block: this.mode === 'push',
			'fixed inset-0 z-50': this.mode === 'overlay',
		}
		const overlayClass = {
			'fixed inset-0 z-49 hidden': true,
		}
		const styleMap = {
			width: this.width,
		}

		return html`
			<nav
				style=${this.styleMap(styleMap)}
				class="${this.classMap({ ...sidebarClasses })}"
				${color({
					bgColor: SchmancyTheme.sys.color.surface.containerLowest,
				})}
			>
				<slot></slot>
			</nav>
			<div
				id="overlay"
				${color({
					bgColor: SchmancyTheme.sys.color.scrim,
				})}
				@click=${this.handleOverlayClick}
				class="${this.classMap({ ...overlayClass })}"
			></div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-nav-drawer-navbar': SchmancyNavigationDrawerSidebar
	}
}
